// Fase C: pagos en linea. Apagado por defecto (VITE_CONDO_ONLINE_PAYMENTS).
// Decision de Producto (2026-10-22): en web solo Stripe Checkout hospedado (checkout_url, https en checkout.stripe.com).
// Sin Stripe.js, sin Payment Element y sin client_secret en el front. Monto del backend; estado por webhook + polling;
// referencias OXXO/SPEI solo en vistas autenticadas.
import type { PayIntentStatus } from '../../../lib/api/condo';

type Env = Record<string, string | boolean | undefined>;
const viteEnv = (): Env => import.meta.env as unknown as Env;

export function isOnlinePaymentsEnabled(env: Env = viteEnv()): boolean {
  return env.VITE_CONDO_ONLINE_PAYMENTS === 'true';
}

/** Polling del estado (UX fase-c.md): cada 5 s, maximo 12 intentos, boton "Consultar ahora". */
export const POLL_INTERVAL_MS = 5000;
export const POLL_MAX_TRIES = 12;
export const FINAL_STATUSES: readonly PayIntentStatus[] = ['succeeded', 'failed', 'canceled', 'expired'];
export const isFinal = (s: PayIntentStatus) => FINAL_STATUSES.includes(s);

/** OXXO: referencia en grupos de 4. */
export function groupOxxo(ref: string): string {
  return ref.replace(/\s+/g, '').replace(/(.{4})(?=.)/g, '$1 ');
}

/** SPEI: CLABE de 18 digitos en grupos 6-4-4-4. */
export function groupClabe(clabe: string): string {
  const d = clabe.replace(/\D/g, '');
  if (d.length !== 18) return clabe;
  return `${d.slice(0, 6)} ${d.slice(6, 10)} ${d.slice(10, 14)} ${d.slice(14)}`;
}

export const CHECKOUT_HOSTS = ['checkout.stripe.com'] as const;
export const VOUCHER_HOSTS = ['payments.stripe.com'] as const;

/** https y host exacto de Stripe (Checkout: checkout.stripe.com; ficha OXXO: payments.stripe.com). */
export function isStripeUrl(url: string | null | undefined, hosts: readonly string[] = CHECKOUT_HOSTS): boolean {
  if (!url) return false;
  try {
    const u = new URL(url);
    return u.protocol === 'https:' && hosts.includes(u.hostname);
  } catch {
    return false;
  }
}

/** Ayer en America/Mexico_City (el job concilia el dia anterior), AAAA-MM-DD. */
export function yesterdayCdmx(now = new Date()): string {
  const d = new Date(now.getTime() - 86_400_000);
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Mexico_City', year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
}

/** Navegacion externa a Stripe Checkout (objeto para poder sustituirlo en pruebas). */
export const externalNav = {
  assign(url: string) {
    window.location.assign(url);
  },
};

/**
 * Al volver de Checkout la pagina se recarga: se recuerda el pay_intent_id (uuid, no es secreto) en sessionStorage
 * para reanudar el polling. Se borra al llegar a un estado final. Hueco: success_url de Backend no lo incluye.
 */
const PENDING_KEY = (unitId: string) => `synclyst.condo.pay.${unitId}`;
export function rememberPayIntent(unitId: string, id: string) {
  try { sessionStorage.setItem(PENDING_KEY(unitId), id); } catch { /* sin storage */ }
}
export function recallPayIntent(unitId: string): string | null {
  try { return sessionStorage.getItem(PENDING_KEY(unitId)); } catch { return null; }
}
export function forgetPayIntent(unitId: string) {
  try { sessionStorage.removeItem(PENDING_KEY(unitId)); } catch { /* sin storage */ }
}
